import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Wrench, CalendarCheck, ShieldCheck, IdCard, ArrowRight, Phone, Megaphone } from "lucide-react";
import CategoryCard from "@/components/CategoryCard";
import ComingSoonCard from "@/components/ComingSoonCard";
import BlogCard from "@/components/BlogCard";
import VehicleCard from "@/components/VehicleCard";
import { useCategories } from "@/hooks/useCategories";
import { useSettings } from "@/hooks/useSettings";
import { useSEO } from "@/hooks/useSEO";
import { API, fileSrc, telHref } from "@/lib/api";

const BENEFIT_ICONS = [Wrench, CalendarCheck, ShieldCheck, IdCard];

const DEFAULT_BENEFITS = [
  { title: "ტექნიკური მომსახურება", text: "ავტომობილის ყველა ტექნიკურ საკითხს კომპანია ფარავს — შენ მხოლოდ მუშაობ." },
  { title: "უფასო კვირა დღეები", text: "კვირა დღე უფასოა — შეგიძლიათ დაისვენოთ ან იმუშაოთ, იჯარას არ იხდით" },
  { title: "სრული დაზღვევა", text: "ყველა ავტომობილი სრული დაზღვევითაა — მშვიდად იმუშავე ქალაქში." },
  { title: "A-კატეგორიის ლიცენზია", text: "ტაქსის ლიცენზიას კომპანია გაძლევს — ბიუროკრატია ჩვენი საზრუნავია." },
];

export default function Home() {
  const meta = useCategories();
  const settings = useSettings();
  const [vehicles, setVehicles] = useState([]);
  const [posts, setPosts] = useState([]);

  useSEO({
    title: "ტაქსის მანქანის ქირაობა თბილისში — Toyota Prius და Tesla | taxipark.ge",
    description: "მანქანის ქირაობა ტაქსისთვის თბილისში: Toyota Prius 50–85 ₾-დან დღიურად, Tesla Model 3 და Model Y. სრული დაზღვევა, ტექნიკური მომსახურება, უფასო კვირები, A-კატეგორიის ლიცენზია.",
    path: "/",
  });

  useEffect(() => {
    axios.get(`${API}/vehicles`).then((r) => setVehicles(r.data)).catch(() => {});
    axios.get(`${API}/blog`).then((r) => setPosts(r.data)).catch(() => {});
  }, []);

  const cats = Object.values(meta)
    .filter((c) => !c.hidden)
    .sort((a, b) => (a.order ?? 99) - (b.order ?? 99));

  const featured = [...vehicles]
    .sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0))
    .filter((v) => v.status === "available")
    .slice(0, 4);

  const benefits = settings.benefits?.length ? settings.benefits : DEFAULT_BENEFITS;
  const heroImg = settings.hero_image_url ? fileSrc(settings.hero_image_url) : null;
  const phone = settings.phone || "591 063 123";
  const phoneHref = telHref(phone);
  const sections = settings.sections?.length ? settings.sections : [
    { id: "intro", enabled: true }, { id: "benefits", enabled: true },
    { id: "featured", enabled: true }, { id: "fleet", enabled: true }, { id: "blog", enabled: true }, { id: "cta", enabled: true },
  ];

  const RENDERERS = {
    intro: (
      <section key="intro" className="py-16 md:py-24" data-testid="intro-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 max-w-3xl">
          <p className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-semibold mb-3">{settings.intro_title || "ვინ ვართ ჩვენ"}</p>
          <p className="text-lg text-gray-300 leading-relaxed" data-testid="intro-text">{settings.intro || ""}</p>
        </div>
      </section>
    ),
    benefits: (
      <section key="benefits" className="pb-16 md:pb-24" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <p className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-semibold mb-3">რატომ ჩვენ</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-white max-w-xl">
            {settings.benefits_title || "ყველაფერი, რაც ტაქსის მძღოლს სჭირდება — ერთ სივრცეში"}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {benefits.slice(0, 4).map((b, i) => {
              const Icon = BENEFIT_ICONS[i] || Wrench;
              return (
                <motion.div
                  key={i}
                  data-testid={`benefit-card-${i}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="card-lux p-7"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/12 flex items-center justify-center mb-5">
                    <Icon className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <h3 className="text-lg font-semibold text-white">{b.title}</h3>
                  <p className="text-sm text-gray-400 mt-2 leading-relaxed">{b.text}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    ),
    featured: featured.length > 0 && (
      <section key="featured" className="pb-16 md:pb-24" data-testid="featured-vehicles-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <p className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-semibold mb-3">ახლა თავისუფალია</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-white">
            {settings.featured_title || "თავისუფალი ავტომობილები ახლავე"}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12" data-testid="featured-vehicles-grid">
            {featured.map((v) => (
              <VehicleCard key={v.id} vehicle={v} category={meta[v.category_id]} />
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link
              to="/tavisufali-manqanebi"
              data-testid="featured-all-available-cta"
              className="btn-gold w-full sm:w-auto text-base !py-4 !px-10"
            >
              ყველა ხელმისაწვდომი ავტომობილი <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    ),
    fleet: (
      <section key="fleet" className="py-16 md:py-24 bg-[#12141C]" data-testid="fleet-preview-section">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-10">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-semibold mb-3">
                {settings.fleet_eyebrow || "Toyota Prius — ჩვენი ავტოპარკის გული"}
              </p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-white">
                {settings.fleet_title || "Prius ყველა ბიუჯეტისთვის, Tesla — პრემიუმისთვის"}
              </h2>
            </div>
            <Link to="/catalog" data-testid="fleet-preview-all-cta" className="btn-ghost shrink-0">
              სრული კატალოგი <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 items-stretch" data-testid="home-fleet-grid">
            {cats.map((cat) => (
              <CategoryCard key={cat.id} category={cat} meta={cat} />
            ))}
            {settings.soon_enabled !== false && <ComingSoonCard settings={settings} banner />}
          </div>
        </div>
      </section>
    ),
    blog: posts.length > 0 && (
      <section key="blog" className="py-16 md:py-24" data-testid="home-blog-section">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-10">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-semibold mb-3">ბლოგი</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-white">
                სიახლეები და რჩევები მძღოლებისთვის
              </h2>
            </div>
            <Link to="/blog" data-testid="home-blog-all-cta" className="btn-ghost shrink-0">
              ყველა სტატია <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid="home-blog-grid">
            {posts.slice(0, 3).map((p, i) => (
              <BlogCard key={p.id} post={p} index={i} />
            ))}
          </div>
        </div>
      </section>
    ),
    cta: (
      <section key="cta" className="py-16 md:py-24" data-testid="home-cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="card-lux p-10 md:p-16 text-center relative overflow-hidden">
            <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#D4AF37]/10 blur-3xl" />
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-white">
              {settings.cta_title || "მზად ხარ დაიწყო?"}
            </h2>
            <p className="text-gray-400 mt-4 max-w-xl mx-auto leading-relaxed">
              {settings.cta_text || ""}
            </p>
            <Link to="/apply" data-testid="home-apply-cta" className="btn-gold mt-8">
              {settings.cta_button || "განაცხადის შევსება"} <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    ),
  };

  return (
    <main data-testid="home-page">
      <section className="relative min-h-[85vh] flex items-center overflow-hidden pt-24 pb-16">
        {heroImg ? (
          <img src={heroImg} alt="taxipark.ge — ტაქსის ავტომობილები ქირით თბილისში" className="absolute inset-0 w-full h-full object-cover" fetchPriority="high" />
        ) : (
          <div className="absolute inset-0 bg-[#0A0B0E]">
            <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-[#D4AF37]/8 blur-3xl" />
            <div className="absolute bottom-0 -left-32 w-[400px] h-[400px] rounded-full bg-white/3 blur-3xl" />
          </div>
        )}
        <div className="hero-overlay absolute inset-0" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 w-full">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-semibold mb-5" data-testid="hero-eyebrow">
              ტაქსის მძღოლებისთვის · თბილისი
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-tight" data-testid="hero-headline">
              {settings.hero_headline || "იქირავე მანქანა, იმუშავე შენი პირობებით"}
            </h1>
            <p className="text-lg text-gray-300 mt-6 leading-relaxed" data-testid="hero-description">
              {settings.hero_description || ""}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-10">
              <Link to="/tavisufali-manqanebi" data-testid="hero-catalog-cta" className="btn-gold text-base">
                {settings.hero_cta || "აირჩიე ავტომობილი"} <ArrowRight className="w-5 h-5" />
              </Link>
              <a href={phoneHref} data-testid="hero-call-cta" className="btn-ghost text-base">
                <Phone className="w-5 h-5" /> დაგვირეკე
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {settings.promo_enabled && (settings.promo_title || settings.promo_text) && (
        <section className="py-10" data-testid="promo-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
            <div className="rounded-2xl border border-[#D4AF37]/40 bg-[#D4AF37]/8 p-8 flex items-start gap-5">
              <Megaphone className="w-8 h-8 text-[#D4AF37] shrink-0 mt-1" />
              <div>
                <h2 className="text-xl font-semibold text-white">{settings.promo_title}</h2>
                <p className="text-gray-300 mt-2 leading-relaxed">{settings.promo_text}</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {sections.filter((s) => s.enabled).map((s) => RENDERERS[s.id]).filter(Boolean)}
    </main>
  );
}
